import React, { useMemo, useState, useRef, useEffect } from "react";
import * as d3 from "d3";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";

const CountryHeatmapDialog = ({
  open,
  onClose,
  countryName,
  countryDeals = [],
}) => {
  const [partnerType, setPartnerType] = useState(""); // Default supply partner
  const svgRef = useRef();
  const containerRef = useRef(); // for tooltip positioning

  const normalizeExact = (str) =>
    str?.toString().trim().toLowerCase().replace(/\s+/g, " ") || "";

  useEffect(()=>{
    setPartnerType("supply")
  },[])
  // Partner lists
  const supplyPartners = useMemo(
    () => [
      ...new Set(
        countryDeals.map((d) => d.supply_partner_name).filter(Boolean)
      ),
    ],
    [countryDeals]
  );
  const brandPartners = useMemo(
    () => [...new Set(countryDeals.map((d) => d.brand_name).filter(Boolean))],
    [countryDeals]
  );
  const agencyPartners = useMemo(
    () => [...new Set(countryDeals.map((d) => d.agency_name).filter(Boolean))],
    [countryDeals]
  );
  const demandPartners = useMemo(
    () => [
      ...new Set(
        countryDeals.map((d) => d.demand_partner_name).filter(Boolean)
      ),
    ],
    [countryDeals]
  );

  const partners = useMemo(() => {
    switch (partnerType) {
      case "brand":
        return brandPartners;
      case "agency":
        return agencyPartners;
      case "demand":
        return demandPartners;
      default:
        return supplyPartners;
    }
  }, [
    partnerType,
    supplyPartners,
    brandPartners,
    agencyPartners,
    demandPartners,
  ]);

  const dealNames = useMemo(() => {
    const relevantDeals = countryDeals.filter((d) => {
      if (partnerType === "supply") return !!d.supply_partner_name;
      if (partnerType === "brand") return !!d.brand_name;
      if (partnerType === "agency") return !!d.agency_name;
      if (partnerType === "demand") return !!d.demand_partner_name;
      return false;
    });
    return [...new Set(relevantDeals.map((d) => d.deal_name).filter(Boolean))];
  }, [countryDeals, partnerType]);

  // Prepare heatmap data
  const heatmapData = useMemo(() => {
    const revenueLookup = {};
    for (const deal of countryDeals) {
      let partnerName =
        partnerType === "supply"
          ? deal.supply_partner_name
          : partnerType === "agency"
          ? deal.agency_name
          : partnerType === "brand"
          ? deal.brand_name
          : deal.demand_partner_name;

      if (!partnerName || !deal.deal_name) continue;
      const key = `${normalizeExact(partnerName)}|${normalizeExact(
        deal.deal_name
      )}`;
      revenueLookup[key] =
        (revenueLookup[key] || 0) + (Number(deal.revenueSorting) || 0);
    }

    return partners.flatMap((partner, y) =>
      dealNames.map((deal, x) => ({
        x,
        y,
        value:
          revenueLookup[`${normalizeExact(partner)}|${normalizeExact(deal)}`] ||
          0,
        partner,
        deal,
      }))
    );
  }, [partners, dealNames, countryDeals, partnerType]);

  // Draw heatmap
  useEffect(() => {
    if (!open) return; // avoid drawing when closed

    const margin = { top: 200, right: 100, bottom: 30, left: 100 };
    const width = dealNames.length * 50 + margin.left + margin.right;
    const height = partners.length * 30 + margin.top + margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    svg.selectAll("*").remove();

    const x = d3
      .scaleBand()
      .range([margin.left, width - margin.right])
      .domain(dealNames)
      .padding(0.05);

    const y = d3
      .scaleBand()
      .range([margin.top, height - margin.bottom])
      .domain(partners)
      .padding(0.05);

    const maxValue = d3.max(heatmapData, (d) => d.value) || 1;

    const color = d3
      .scaleSequential()
      .interpolator(d3.interpolateReds)
      .domain([0, maxValue]);

    // Axes
    svg
      .append("g")
      .attr("transform", `translate(0,${margin.top})`)
      .call(d3.axisTop(x))
      .selectAll("text")
      .style("text-anchor", "start")
      .attr("transform", "rotate(-60)")
      .attr("dx", "0.5em")
      .attr("dy", "-0.5em");

    svg
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .call(d3.axisLeft(y));

    // Remove old tooltip inside container
    d3.select(containerRef.current).selectAll(".d3-tooltip").remove();

    // Tooltip inside dialog container
    const tooltip = d3
      .select(containerRef.current)
      .append("div")
      .attr("class", "d3-tooltip")
      .style("position", "absolute")
      .style("background", "white")
      .style("border", "1px solid #ccc")
      .style("padding", "6px 8px")
      .style("border-radius", "4px")
      .style("box-shadow", "0 2px 4px rgba(0,0,0,0.2)")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", 2000);

    // Rects
    svg
      .selectAll()
      .data(heatmapData)
      .enter()
      .append("rect")
      .attr("x", (d) => x(d.deal))
      .attr("y", (d) => y(d.partner))
      .attr("width", x.bandwidth())
      .attr("height", y.bandwidth())
      .style("fill", (d) => color(d.value))
      .on("mouseover", function (event, d) {
        tooltip
          .transition()
          .duration(100)
          .style("opacity", 1);
        tooltip
          .html(
            `<div><b>Partner:</b> ${d.partner}</div>
             <div><b>Deal:</b> ${d.deal}</div>
             <div><b>Advertiser Cost:</b> $${d.value.toLocaleString()}</div>`
          )
          .style("left", event.offsetX + 20 + "px")
          .style("top", event.offsetY - 20 + "px");
      })
      .on("mousemove", function (event) {
        tooltip
          .style("left", event.offsetX + 20 + "px")
          .style("top", event.offsetY - 20 + "px");
      })
      .on("mouseout", function () {
        tooltip.transition().duration(200).style("opacity", 0);
      });
  }, [heatmapData, partners, dealNames, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>{countryName} - Deals Heatmap</DialogTitle>
      <DialogContent
        sx={{
          height: "70vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Dropdown left aligned */}
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          {/* <Typography variant="body2" sx={{ minWidth: 150 }}>
            Partner Type:
          </Typography> */}
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value)}
            >
              <MenuItem value="supply">Supply Partner</MenuItem>
              <MenuItem value="brand">Brand</MenuItem>
              <MenuItem value="agency">Agency</MenuItem>
              <MenuItem value="demand">Demand Partner</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Heatmap container with tooltip */}
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
          }}
        >
          <svg ref={svgRef}></svg>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountryHeatmapDialog;

